export function decodeEntities(str) {
  let s = String(str == null ? '' : str);
  const pairs = [
    ['&amp;', '&'],
    ['&lt;', '<'],
    ['&gt;', '>'],
    ['&quot;', '"'],
    ['&#39;', "'"],
    ['&#039;', "'"],
    ['&apos;', "'"],
    ['&#x27;', "'"],
    ['&#X27;', "'"],
    ['‘', "'"], // ‘
    ['’', "'"], // ’
    ['“', '"'], // “
    ['”', '"'] // ”
  ];
  for (const [from, to] of pairs) s = s.split(from).join(to);
  return s;
}

function isBareField(s) {
  if (!s) return false;
  return !(
    s.includes('=') ||
    s.includes('!') ||
    s.includes('<') ||
    s.includes('>')
  );
}

function firstToken(t) {
  for (let i = 0; i < t.length; i += 1) {
    const c = t[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r')
      return t.slice(0, i);
  }
  return t;
}

function parseValue(rawValue) {
  const t = String(rawValue).trim();
  if (
    t.length >= 2 &&
    ((t[0] === "'" && t[t.length - 1] === "'") ||
      (t[0] === '"' && t[t.length - 1] === '"'))
  ) {
    return t.slice(1, -1);
  }
  return firstToken(t);
}

function firstQuoteIndex(s) {
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === "'" || s[i] === '"') return i;
  }
  return -1;
}

const TEXT_OPS = [
  [' does not contain ', 'notContains'],
  [' contains ', 'contains'],
  [' starts with ', 'startsWith']
];
const NUM_OPS = [
  ['>=', 'gte'],
  ['<=', 'lte'],
  ['>', 'gt'],
  ['<', 'lt']
];

export function parseCondition(expr) {
  const s = decodeEntities(expr).trim();
  const lower = s.toLowerCase();

  if (lower.endsWith(' is not empty')) {
    return {
      field: s.slice(0, s.length - ' is not empty'.length).trim(),
      op: 'notEmpty'
    };
  }
  if (lower.endsWith(' is empty')) {
    return {
      field: s.slice(0, s.length - ' is empty'.length).trim(),
      op: 'empty'
    };
  }

  const quote = firstQuoteIndex(s);
  const head = quote === -1 ? s : s.slice(0, quote);
  const headLower = head.toLowerCase();

  const matchInfix = (table, caseSensitive) => {
    const hay = caseSensitive ? head : headLower;
    for (const [token, op] of table) {
      const idx = hay.indexOf(token);
      if (idx !== -1) {
        return {
          field: s.slice(0, idx).trim(),
          op,
          value: parseValue(s.slice(idx + token.length))
        };
      }
    }
    return null;
  };

  const textHit = matchInfix(TEXT_OPS, false);
  if (textHit) return textHit;

  const eqIdx = head.indexOf('==');
  if (eqIdx !== -1) {
    return {
      field: s.slice(0, eqIdx).trim(),
      op: 'eq',
      value: parseValue(s.slice(eqIdx + 2))
    };
  }
  const neIdx = head.indexOf('!=');
  if (neIdx !== -1) {
    return {
      field: s.slice(0, neIdx).trim(),
      op: 'ne',
      value: parseValue(s.slice(neIdx + 2))
    };
  }

  const numHit = matchInfix(NUM_OPS, true);
  if (numHit) return numHit;

  if (isBareField(s)) return { field: s, op: 'truthy' };
  return null;
}

/**
 * Split source into an ordered stream of text and `{{ }}` mustache spans with
 * a linear scan (no regex). Each mustache carries the trimmed `inner` text and
 * the exact `raw` slice (delimiters included) for verbatim re-emission.
 * @param {string} src
 * @returns {Array<{type:'text',value:string}|{type:'mustache',inner:string,raw:string}>}
 */
export function scanMustaches(src) {
  const s = String(src == null ? '' : src);
  const out = [];
  let i = 0;
  while (i < s.length) {
    const open = s.indexOf('{{', i);
    if (open === -1) {
      if (i < s.length) out.push({ type: 'text', value: s.slice(i) });
      break;
    }
    if (open > i) out.push({ type: 'text', value: s.slice(i, open) });
    const close = s.indexOf('}}', open + 2);
    if (close === -1) {
      // No closing braces — treat the remainder as literal text (malformed).
      out.push({ type: 'text', value: s.slice(open) });
      break;
    }
    out.push({
      type: 'mustache',
      inner: s.slice(open + 2, close).trim(),
      raw: s.slice(open, close + 2)
    });
    i = close + 2;
  }
  return out;
}

/** Classify a mustache's inner text into a control/field node. */
export function classifyMustache(inner) {
  const lower = inner.toLowerCase();
  if (lower.startsWith('#if')) {
    return { kind: 'openIf', raw: inner, cond: parseCondition(inner.slice(3)) };
  }
  if (lower.startsWith('#each')) {
    return {
      kind: 'openEach',
      raw: inner,
      collection: decodeEntities(inner.slice(5)).trim()
    };
  }
  if (lower === 'else') return { kind: 'else', raw: inner };
  if (lower === '/if') return { kind: 'closeIf', raw: inner };
  if (lower === '/each') return { kind: 'closeEach', raw: inner };
  return { kind: 'field', raw: inner, name: decodeEntities(inner) };
}

export function hbStr(value) {
  let out = '';
  for (const ch of String(value)) {
    if (ch === '\\') out += '\\\\';
    else if (ch === '"') out += '\\"';
    else out += ch;
  }
  return `"${out}"`;
}

export function escapeLiteralText(value) {
  return String(value).split('{{').join('\\{{');
}

/** After-`#if` remainder with the `#if` keyword + leading whitespace stripped. */
export function stripIfPrefix(raw) {
  let s = String(raw).slice(3);
  while (s.length && (s[0] === ' ' || s[0] === '\t')) s = s.slice(1);
  return s;
}

/**
 * Transpile a friendly-DSL template into canonical Handlebars source.
 * @param {string} html
 * @returns {{ code:string, nodes:object[], errors:Array<{type:string,raw:string,condText?:string}> }}
 */
export function transpileToHandlebars(html) {
  const spans = scanMustaches(html);
  let code = '';
  const nodes = [];
  const errors = [];

  for (const span of spans) {
    if (span.type === 'text') {
      code += escapeLiteralText(span.value);
      nodes.push({ type: 'text', value: span.value });
      continue;
    }
    const node = classifyMustache(span.inner);
    nodes.push(node);
    switch (node.kind) {
      case 'field':
        code += `{{lmField ${hbStr(node.name)}}}`;
        break;
      case 'openIf': {
        if (!node.cond) {
          errors.push({
            type: 'bad-condition',
            raw: node.raw,
            condText: stripIfPrefix(node.raw)
          });
          code += `{{#lmIf ${hbStr('')} ${hbStr('truthy')}}}`;
        } else {
          const parts = [
            '{{#lmIf',
            hbStr(node.cond.field),
            hbStr(node.cond.op)
          ];
          if (node.cond.value !== undefined) parts.push(hbStr(node.cond.value));
          code += `${parts.join(' ')}}}`;
        }
        break;
      }
      case 'openEach':
        code += `{{#lmEach ${hbStr(node.collection)}}}`;
        break;
      case 'else':
        code += '{{else}}';
        break;
      case 'closeIf':
        code += '{{/lmIf}}';
        break;
      case 'closeEach':
        code += '{{/lmEach}}';
        break;
      default:
        break;
    }
  }

  return { code, nodes, errors };
}
